// Reusable widget-grid layout state with userSettings persistence (extracted
// from performance-v2.tsx, #4932 Phase 0.4).
//
// The layout lives in a caller-supplied Jotai atom (in-memory, per app
// convention — no localStorage) so it survives in-session navigation, is
// hydrated once from userSettings.widget_layouts, and is written back
// debounced (1s) after any reorder/resize. Returns plain accessors —
// never components.
import { useCallback, useEffect, useRef } from 'react';
import { useAtom } from 'jotai';
import type { PrimitiveAtom } from 'jotai';
import { useQuery } from '@tanstack/react-query';

import type { WidgetConfig } from '@/components/glitch-ui/widget-grid';
import { userSettingsQueryOptions, useUpdateUserSettingsMutation } from '@/lib/query-options';

export function useWidgetLayout(
  layoutAtom: PrimitiveAtom<Record<string, WidgetConfig[]>>,
  defaults: Record<string, WidgetConfig[]>,
  // Explicitly retired widget ids: dropped from saved layouts, which persist
  // whole to user settings and would otherwise resurrect killed widgets
  // forever. Unlisted unknown ids stay grandfathered (panels may still
  // render ids that left the defaults, e.g. 'budget' under Audience).
  retiredIds?: ReadonlySet<string>,
): {
  getTabLayout: (tabId: string) => WidgetConfig[];
  handleReorder: (tabId: string, layout: WidgetConfig[]) => void;
  handleResize: (tabId: string, id: string, width: 33 | 50 | 66 | 100) => void;
  resetTabLayout: (tabId: string) => void;
} {
  const [allLayouts, setAllLayouts] = useAtom(layoutAtom);
  const { data: userSettings } = useQuery(userSettingsQueryOptions());
  const settingsMutation = useUpdateUserSettingsMutation();
  const layoutInitialized = useRef(false);
  const skipNextSave = useRef(false);

  // Initialize layouts from persisted settings
  useEffect(() => {
    if (userSettings && !layoutInitialized.current) {
      if (userSettings.widget_layouts) {
        skipNextSave.current = true;
        setAllLayouts(userSettings.widget_layouts as Record<string, WidgetConfig[]>);
      }
      layoutInitialized.current = true;
    }
  }, [userSettings, setAllLayouts]);

  // Debounced save of layout changes (1s)
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => {
    if (!layoutInitialized.current) return;
    if (skipNextSave.current) { skipNextSave.current = false; return; }

    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => {
      if (Object.keys(allLayouts).length > 0) {
        settingsMutation.mutate({ widget_layouts: allLayouts });
      }
    }, 1000);

    return () => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    };
  }, [allLayouts]);

  // Saved layouts keep their arrangement, minus explicitly retired ids;
  // widgets newly added to the defaults append after the saved ones instead
  // of never appearing for accounts with a persisted layout.
  const getTabLayout = useCallback(
    (tabId: string) => {
      const defaultsForTab = defaults[tabId] || [];
      const stored = allLayouts[tabId];
      if (!stored) return defaultsForTab;
      const kept = retiredIds ? stored.filter((w) => !retiredIds.has(w.id)) : stored;
      const keptIds = new Set(kept.map((w) => w.id));
      const maxOrder = kept.reduce((m, w) => Math.max(m, w.order), -1);
      const added = defaultsForTab
        .filter((w) => !keptIds.has(w.id))
        .map((w, i) => ({ ...w, order: maxOrder + 1 + i }));
      return [...kept, ...added];
    },
    [allLayouts, defaults, retiredIds],
  );

  const handleReorder = useCallback((tabId: string, layout: WidgetConfig[]) => {
    setAllLayouts(prev => ({ ...prev, [tabId]: layout }));
  }, [setAllLayouts]);

  const handleResize = useCallback((tabId: string, id: string, width: 33 | 50 | 66 | 100) => {
    setAllLayouts(prev => {
      const current = prev[tabId] || defaults[tabId] || [];
      return {
        ...prev,
        [tabId]: current.map(w => w.id === id ? { ...w, width } : w),
      };
    });
  }, [setAllLayouts]);

  // "Reset page layout": write the CURRENT defaults into the tab explicitly.
  // The settings PATCH deep-merges dicts, so deleting the key locally would
  // leave the stale layout in userSettings and it would rehydrate right back;
  // an explicit list replaces it (lists are replaced, not merged).
  const resetTabLayout = useCallback((tabId: string) => {
    const defaultsForTab = defaults[tabId];
    if (!defaultsForTab) return;
    setAllLayouts(prev => ({ ...prev, [tabId]: defaultsForTab.map(w => ({ ...w })) }));
  }, [setAllLayouts, defaults]);

  return { getTabLayout, handleReorder, handleResize, resetTabLayout };
}
